import React, { useState, useEffect } from "react";
import { Logo } from "@component/logo";
import { Button } from "@component/button";
import { RegisterJobSeekerInput } from "@type/auth";
import { ButtonSignupGoogle } from "@component/button-signup-google";
import { TokenResponse } from "@react-oauth/google";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { isValidEmail } from "@util/string";
import { useCompanySubdomain } from "@context/subdomain_context";
import { getCookie, setCookie } from "@util/cookie";
import { data } from "react-router";

interface JobseekerFirstTimeRegisterProps {
    error: string | null;
    onRegister: (user: RegisterJobSeekerInput) => void;
    onGoogleSignup: (tokenResponse: TokenResponse) => void;
    email: string;
    setEmail: React.Dispatch<React.SetStateAction<string>>;
    password: string;
    setPassword: React.Dispatch<React.SetStateAction<string>>;
    confirmPassword: string;
    setConfirmPassword: React.Dispatch<React.SetStateAction<string>>;
    isAuthGoogleLoading: boolean
}

export function JobseekerFirstTimeRegister({
    error,
    onRegister,
    onGoogleSignup,
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    isAuthGoogleLoading
}: JobseekerFirstTimeRegisterProps) {
    const [isLoadingCompanyInfo, companyInfo] = useCompanySubdomain()
    const [emailError, setEmailError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Validate passwords when either password or confirmPassword changes
    useEffect(() => {
        if (password === "") return;
        if ((password || "").length < 8) {
            setPasswordError("Password must be at least 8 characters.")
            return
        }

        if (confirmPassword && password !== confirmPassword) {
            setConfirmPasswordError("Passwords don't match.");
        } else {
            setConfirmPasswordError(null);
            setPasswordError(null);
        }
    }, [password, confirmPassword]);

    useEffect(() => {
        if (email !== "" && !isValidEmail(email)) {
            setEmailError("Please enter a valid email address.");
        } else {
            setEmailError(null);
        }
    }, [email])

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const toggleShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (email && password && password === confirmPassword) {
            const dataApplyJobGuest = getCookie('__apply_job_guest__');
            if (!dataApplyJobGuest) {
                setCookie('__apply_job_guest__', JSON.stringify({
                    email: email,
                    country_code: "",
                    phone: ""
                }))
            } else {
                setCookie('__apply_job_guest__', JSON.stringify({
                    email: email,
                    ...dataApplyJobGuest,
                }))
            }
            onRegister({ email, password, first_name: "", last_name: "", confirm_password: password });
        } else if (password !== confirmPassword) {
            setConfirmPasswordError("Passwords don't match.");
        }
    };

    return (
        <div className="max-w-2xl sm:max-w-3xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-8">
            <div className="flex flex-col items-center mb-6">
                {companyInfo ? (
                    <div className="h-16">
                        <img
                            className="h-16"
                            src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${companyInfo?.preferences?.company_logo}`}
                            onError={(e) => {
                                e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`;
                            }}
                        />
                    </div>
                ) : (
                    <div className="w-14 h-14">
                        <img
                            src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
                        />
                    </div>
                )}
            </div>
            <h2 className="text-2xl font-bold text-center mb-6 text-gray-900 dark:text-white">
                Create your account
            </h2>
            {error && (
                <p className="mb-4 text-sm text-red-600 dark:text-red-500">
                    {error}
                </p>
            )}
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                        Email address
                    </label>
                    <input
                        type="text"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                        required
                    />
                    {emailError && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-500">
                            {emailError}
                        </p>
                    )}
                </div>

                <div className="mb-4">
                    <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                        Password
                    </label>
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className={`w-full px-3.5 py-2.5 border ${passwordError ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white`}
                            required
                        />
                        <button
                            type="button"
                            onClick={toggleShowPassword}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            {showPassword ? (
                                <EyeOffIcon className="h-4 w-4" />
                            ) : (
                                <EyeIcon className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {passwordError && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-500">
                            {passwordError}
                        </p>
                    )}
                </div>
                <div className="mb-4">
                    <label
                        htmlFor="confirmPassword"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                        Confirm Password
                    </label>
                    <div className="relative">
                        <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="••••••••"
                            className={`w-full px-3.5 py-2.5 border ${passwordError ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white`}
                            required
                        />
                        <button
                            type="button"
                            onClick={toggleShowConfirmPassword}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            {showConfirmPassword ? (
                                <EyeOffIcon className="h-4 w-4" />
                            ) : (
                                <EyeIcon className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {confirmPasswordError && (
                        <p className="mt-2 text-sm text-red-600 dark:text-red-500">
                            {confirmPasswordError}
                        </p>
                    )}
                </div>
                <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-md mb-4 text-base"
                    disabled={isAuthGoogleLoading}
                >
                    Register
                </Button>
            </form>
            {(getCookie("CURRENT_COMPANY") || "") === "" && (
                <>
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300 dark:border-gray-700" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white dark:bg-gray-900 text-gray-500">
                                Or continue with
                            </span>
                        </div>
                    </div>
                    <ButtonSignupGoogle
                        label="Sign up with Google"
                        isLoading={isAuthGoogleLoading}
                        onClick={(tokenResponse) => onGoogleSignup(tokenResponse)}
                    />
                </>
            )}
            <p className="mt-6 text-xs text-center text-gray-500 dark:text-gray-400">
                By signing up, you agree to our{" "}
                <a
                    href="https://ourteam.ai/terms-of-service"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                >
                    Terms of Service
                </a>{" "}
                <br />and{" "}
                <a
                    href="https://ourteam.ai/privacy-policy"
                    className="text-blue-700 hover:underline"
                    target="_blank"
                >
                    Privacy Policy.
                </a>
            </p>
        </div>
    );
}
